import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, User, Pencil, Upload, X, Anchor, Activity, Eye, EyeOff } from "lucide-react";
import { api, fileUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { YACHT_RANKS } from "@/lib/yachtRanks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

// Compact "5m ago" / "2h ago" formatter used only in Developer-visible activity cells.
function relTime(iso) {
  if (!iso) return "—";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    if (diff < 30 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return "—"; }
}
function fmtActive(ms) {
  if (!ms || ms < 60_000) return "< 1 min";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const mr = m % 60;
  return mr ? `${h}h ${mr}m` : `${h}h`;
}
const isOnline = (iso) => {
  if (!iso) return false;
  try { return (Date.now() - new Date(iso).getTime()) < 3 * 60_000; } catch { return false; }
};

const emptyForm = { id: null, email: "", password: "", confirm_password: "", name: "", crew_id: "", role: "crew", photo_path: "", rank: "", can_create_manual_bookings: false, engaged_modules: [] };

export default function UsersPanel() {
  const { admin } = useAuth();
  const meId = admin?.id;
  const myRole = admin?.role;
  const iAmDev = myRole === "developer";
  const iAmAdmin = myRole === "admin";
  const iAmManager = myRole === "manager";
  // What roles can I assign / edit?
  const assignableRoles = iAmDev
    ? ["developer", "admin", "manager", "supervisor", "crew", "spa_ops", "pantry"]
    : iAmAdmin
      ? ["manager", "supervisor", "crew"]
      : iAmManager
        ? ["supervisor", "crew"]
        : [];
  const canEditTarget = (targetRole) => {
    if (iAmDev) return true; // Developer can touch every role incl. spa_ops
    if (iAmAdmin) return ["manager", "supervisor", "crew"].includes(targetRole);
    if (iAmManager) return ["supervisor", "crew"].includes(targetRole);
    return false;
  };
  const [users, setUsers] = useState([]);
  const [crew, setCrew] = useState([]);
  const [modules, setModules] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const isEditing = !!form.id;

  // Wrap `load` in useCallback so `useEffect`/`setInterval` closures capture a
  // stable reference — satisfies `react-hooks/exhaustive-deps` and prevents
  // stale-data bugs when the component re-renders during a live update.
  const load = useCallback(async () => {
    const [u, c, m] = await Promise.all([api.get("/admin/users"), api.get("/admin/crew"), api.get("/admin/modules")]);
    setUsers(u.data || []);
    setCrew(c.data || []);
    setModules(m.data?.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  // ---- LIVE user activity via WebSocket ----
  // Any `users.activity` broadcast (from login OR heartbeat) patches the user
  // row inline so the developer sees engagement counters, online status, and
  // IP fingerprint update in real-time without a re-fetch.
  useLiveEvents((ev) => {
    if (!ev || !ev.type) return;
    if (ev.type === "users.activity" && ev.user?.id) {
      setUsers((prev) => prev.map((u) => {
        if (u.id !== ev.user.id) return u;
        const merged = { ...u };
        for (const k of ["last_login_at", "last_active_at", "last_login_ip",
                         "last_login_local_ip", "last_local_ip",
                         "last_login_device_id", "last_login_user_agent"]) {
          if (ev.user[k] !== undefined && ev.user[k] !== null) merged[k] = ev.user[k];
        }
        if (typeof ev.user.active_ms_delta === "number") {
          merged.total_active_ms = (u.total_active_ms || 0) + ev.user.active_ms_delta;
        }
        if (ev.user.kind === "login") {
          merged.total_logins = (u.total_logins || 0) + 1;
        }
        return merged;
      }));
    } else if (ev.type === "users.updated" || ev.type === "users.deleted" || ev.type === "users.created") {
      // Structural changes — cheapest correct thing is a full re-fetch.
      load();
    }
  });

  // Tick every 30s to refresh the "just now / 2m ago" relative labels even
  // when no new heartbeat arrives (e.g. viewer tab has been idle looking).
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  // Bulletproof live fallback: even if the WebSocket drops or a broadcast is
  // missed, re-fetch every 10 s so activity/online status is never stale.
  useEffect(() => {
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [load]);

  const openNew = () => {
    const defaultRole = assignableRoles.includes("crew") ? "crew" : (assignableRoles[0] || "crew");
    setForm({ ...emptyForm, role: defaultRole });
    setOpen(true);
  };
  const openEdit = (u) => {
    setForm({
      id: u.id,
      email: u.email || "",
      password: "",
      confirm_password: "",
      name: u.name || "",
      crew_id: u.crew_id || "",
      role: u.role || "crew",
      photo_path: u.photo_path || "",
      rank: u.rank || "",
      can_create_manual_bookings: !!u.can_create_manual_bookings,
      engaged_modules: Array.isArray(u.engaged_modules) ? u.engaged_modules : [],
    });
    setOpen(true);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_path: data.path }));
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Photo upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    if (form.password && form.password !== form.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    if (!isEditing && (!form.password || form.password.length < 6)) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      if (isEditing) {
        const payload = {
          name: form.name.trim(),
          email: form.email.trim(),
          role: form.role || "crew",
          crew_id: form.crew_id || null,
          photo_path: form.photo_path || null,
          rank: form.rank || null,
          can_create_manual_bookings: !!form.can_create_manual_bookings,
          engaged_modules: form.engaged_modules || [],
          ...(form.password ? { password: form.password } : {}),
        };
        await api.put(`/admin/users/${form.id}`, payload);
        toast.success("Account updated");
      } else {
        await api.post("/admin/users", {
          email: form.email.trim(),
          password: form.password,
          name: form.name.trim(),
          crew_id: form.crew_id || null,
          role: form.role || "crew",
          rank: form.rank || null,
          can_create_manual_bookings: !!form.can_create_manual_bookings,
          engaged_modules: form.engaged_modules || [],
        });
        toast.success(form.role === "admin" ? "Admin account created" : "Crew account created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not save account"); }
    finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not remove account"); }
  };

  const avatar = (u) => (
    <div className="w-9 h-9 rounded-full bg-muted overflow-hidden flex items-center justify-center">
      {u.photo_path ? (
        <img src={fileUrl(u.photo_path)} alt={u.name} className="w-full h-full object-cover" data-testid={`user-photo-${u.id}`} />
      ) : u.role === "developer" ? (
        <ShieldCheck className="h-4 w-4 text-destructive" />
      ) : u.role === "admin" ? (
        <ShieldCheck className="h-4 w-4 text-accent" />
      ) : u.role === "manager" || u.role === "supervisor" ? (
        <ShieldCheck className="h-4 w-4 text-primary/70" />
      ) : (
        <User className="h-4 w-4 text-primary/60" />
      )}
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-display text-3xl">Users</h2>
          <p className="text-sm text-muted-foreground mt-1">Owner and crew accounts that can access this dashboard.</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-primary hover:bg-primary/90" data-testid="new-user-btn">
          <Plus className="h-4 w-4 mr-2" /> Add account
        </Button>
      </div>

      <div className="bg-card border border-border overflow-x-auto" data-testid="users-list">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left">
              <th className="p-4 overline text-primary/50 font-normal">User</th>
              <th className="p-4 overline text-primary/50 font-normal">Email</th>
              <th className="p-4 overline text-primary/50 font-normal">Role</th>
              <th className="p-4 overline text-primary/50 font-normal">Rank</th>
              <th className="p-4 overline text-primary/50 font-normal">Linked crew</th>
              {iAmDev && <th className="p-4 overline text-primary/50 font-normal">Last login</th>}
              {iAmDev && <th className="p-4 overline text-primary/50 font-normal">Last active</th>}
              {iAmDev && <th className="p-4 overline text-primary/50 font-normal">Engagement</th>}
              {iAmDev && <th className="p-4 overline text-primary/50 font-normal">IP · device</th>}
              <th className="p-4 overline text-primary/50 font-normal"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const linked = crew.find((c) => c.id === u.crew_id);
              const online = isOnline(u.last_active_at);
              return (
                <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {avatar(u)}
                      <span className="font-medium">{u.name}</span>
                    </div>
                  </td>
                  <td className="p-4 text-muted-foreground">{u.email}</td>
                  <td className="p-4">
                    <Badge
                      variant={u.role === "developer" || u.role === "admin" ? "default" : "outline"}
                      className={
                        u.role === "developer" ? "bg-destructive"
                          : u.role === "admin" ? "bg-primary"
                          : (u.role === "manager" || u.role === "supervisor") ? "border-accent/60 text-accent"
                          : u.role === "spa_ops" ? "border-accent/40 text-accent bg-accent/5"
                          : ""
                      }
                    >
                      {u.role}
                    </Badge>
                  </td>
                  <td className="p-4 text-muted-foreground" data-testid={`user-rank-${u.id}`}>
                    {u.rank ? (
                      <span className="inline-flex items-center gap-1.5 text-foreground/80">
                        <Anchor className="h-3 w-3 text-accent" strokeWidth={2} />
                        {u.rank}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="p-4 text-muted-foreground">{linked?.name || "—"}</td>
                  {iAmDev && (
                    <td className="p-4 text-xs text-muted-foreground" data-testid={`user-lastlogin-${u.id}`}>
                      <div>{relTime(u.last_login_at)}</div>
                      {u.total_logins ? <div className="text-[10px] opacity-60">{u.total_logins} total</div> : null}
                    </td>
                  )}
                  {iAmDev && (
                    <td className="p-4 text-xs" data-testid={`user-active-${u.id}`}>
                      <div className="flex items-center gap-1.5">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${online ? "bg-emerald-500 animate-pulse" : "bg-muted-foreground/30"}`} />
                        <span className={online ? "text-emerald-600 font-medium" : "text-muted-foreground"}>
                          {online ? "Online now" : relTime(u.last_active_at)}
                        </span>
                      </div>
                    </td>
                  )}
                  {iAmDev && (
                    <td className="p-4 text-xs text-muted-foreground font-mono" data-testid={`user-engagement-${u.id}`}>
                      {fmtActive(u.total_active_ms)}
                    </td>
                  )}
                  {iAmDev && (
                    <td className="p-4 text-[10px] text-muted-foreground font-mono max-w-[260px] break-all" data-testid={`user-fingerprint-${u.id}`}>
                      {u.last_login_ip ? (
                        <>
                          {u.last_login_device_family && (
                            <div className="text-foreground/80 mb-0.5">{u.last_login_device_family}</div>
                          )}
                          {u.last_login_device_id && u.known_devices?.[u.last_login_device_id]?.label && (
                            <div className="text-accent mb-0.5">"{u.known_devices[u.last_login_device_id].label}"</div>
                          )}
                          <div><span className="text-primary/60">WAN</span> {u.last_login_ip}</div>
                          {(u.last_local_ip || u.last_login_local_ip) ? (
                            <div><span className="text-primary/60">LAN</span> {u.last_local_ip || u.last_login_local_ip}</div>
                          ) : (
                            <div className="opacity-50" title="Chrome/Edge mDNS-obfuscate LAN IPs on HTTPS. Safari & Firefox usually work; on Chrome disable chrome://flags/#enable-webrtc-hide-local-ips-with-mdns">
                              <span className="text-primary/60">LAN</span> hidden
                            </div>
                          )}
                          {u.last_login_device_id && <div className="opacity-60">dev {u.last_login_device_id.slice(0, 8)}</div>}
                        </>
                      ) : "—"}
                    </td>
                  )}
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      {canEditTarget(u.role) && (
                        <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                      )}
                      {u.id !== meId && canEditTarget(u.role) && (
                        <Button size="sm" variant="outline" onClick={() => remove(u)} className="text-destructive border-destructive/40" data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {users.length === 0 && (
              <tr><td colSpan={iAmDev ? 10 : 6} className="p-10 text-center text-muted-foreground">No users yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {isEditing ? "Edit account" : "New account"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="overline text-primary/60">Profile photo</Label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-muted overflow-hidden border border-border flex items-center justify-center" data-testid="uf-photo-preview">
                  {form.photo_path ? (
                    <img src={fileUrl(form.photo_path)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input ref={fileRef} type="file" accept="image/*" onChange={upload} hidden data-testid="uf-photo-input" />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="uf-photo-upload"
                  >
                    <Upload className="h-3.5 w-3.5 mr-2" /> {uploading ? "Uploading…" : (form.photo_path ? "Replace photo" : "Upload photo")}
                  </Button>
                  {form.photo_path && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setForm({ ...form, photo_path: "" })}
                      className="text-destructive"
                      data-testid="uf-photo-remove"
                    >
                      <X className="h-3.5 w-3.5 mr-1" /> Remove
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Square images look best. JPG, PNG, WEBP.</p>
            </div>

            <div>
              <Label className="overline text-primary/60">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-2" data-testid="uf-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {assignableRoles.includes("crew") && <SelectItem value="crew" data-testid="uf-role-crew">Crew · view only (schedule & content)</SelectItem>}
                  {assignableRoles.includes("supervisor") && <SelectItem value="supervisor" data-testid="uf-role-supervisor">Supervisor · full ops, no user management</SelectItem>}
                  {assignableRoles.includes("manager") && <SelectItem value="manager" data-testid="uf-role-manager">Manager · full ops + manages Supervisor & Crew</SelectItem>}
                  {assignableRoles.includes("admin") && <SelectItem value="admin" data-testid="uf-role-admin">Admin · full access + manages Manager/Supervisor/Crew</SelectItem>}
                  {assignableRoles.includes("developer") && <SelectItem value="developer" data-testid="uf-role-developer">Developer · super-admin + platform tools</SelectItem>}
                  {assignableRoles.includes("spa_ops") && <SelectItem value="spa_ops" data-testid="uf-role-spa-ops">Spa Ops iPad · fullscreen kiosk (no dashboard)</SelectItem>}
                  {assignableRoles.includes("pantry") && <SelectItem value="pantry" data-testid="uf-role-pantry">Pantry iPad · fullscreen kiosk (no dashboard)</SelectItem>}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Crew: view only · Supervisor: full ops, no user mgmt · Manager: ops + Supervisor/Crew mgmt · Admin: Manager+ mgmt · Developer: super-admin.
              </p>
            </div>
            <div>
              <Label className="overline text-primary/60">Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="uf-name" className="mt-2" />
            </div>
            <div>
              <Label className="overline text-primary/60">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="uf-email" className="mt-2" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="overline text-primary/60">
                  {isEditing ? "New password (leave empty to keep current)" : "Password (min 6 chars)"}
                </Label>
                <div className="relative mt-2">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="uf-password"
                    className="pr-10"
                    autoComplete="new-password"
                    placeholder={isEditing ? "Only enter to change" : ""}
                  />
                  <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? <EyeOff className="h-4 w-4"/> : <Eye className="h-4 w-4"/>}
                  </button>
                </div>
              </div>
              <div>
                <Label className="overline text-primary/60">Confirm password</Label>
                <div className="relative mt-2">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    value={form.confirm_password || ""}
                    onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                    data-testid="uf-confirm-password"
                    className={`pr-10 ${form.confirm_password && form.password !== form.confirm_password ? "border-destructive" : ""}`}
                    autoComplete="new-password"
                    placeholder={isEditing ? "Repeat new password" : "Repeat password"}
                  />
                  <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground" onClick={() => setShowConfirmPassword((v) => !v)} aria-label={showConfirmPassword ? "Hide password" : "Show password"}>
                    {showConfirmPassword ? <EyeOff className="h-4 w-4"/> : <Eye className="h-4 w-4"/>}
                  </button>
                </div>
                {form.confirm_password && form.password !== form.confirm_password && <p className="text-xs text-destructive mt-1">Passwords must match.</p>}
              </div>
            </div>
            <div>
              <Label className="overline text-primary/60">Link to crew profile (optional)</Label>
              <Select value={form.crew_id || "none"} onValueChange={(v) => setForm({ ...form, crew_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-2" data-testid="uf-crew"><SelectValue placeholder="No link" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— none —</SelectItem>
                  {crew.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.role}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">When linked, service coverage and Apple Watch alerts are inherited automatically from that crew profile.</p>
            </div>
            {(form.role === "supervisor" || form.role === "spa_ops" || form.role === "pantry") && (
              <div className="border border-accent/30 bg-accent/5 p-3 flex items-start gap-3" data-testid="uf-manual-permission">
                <input
                  id="uf-can-manual"
                  type="checkbox"
                  checked={!!form.can_create_manual_bookings}
                  onChange={(e) => setForm({ ...form, can_create_manual_bookings: e.target.checked })}
                  className="mt-1 h-4 w-4"
                  data-testid="uf-can-manual-cb"
                />
                <label htmlFor="uf-can-manual" className="text-sm">
                  <span className="font-medium">Allow manual bookings</span>
                  <span className="block text-[11px] text-muted-foreground mt-0.5">Lets this {form.role === "spa_ops" ? "Spa Ops iPad" : form.role === "pantry" ? "Pantry iPad" : "Supervisor"} record walk-in / verbal bookings on behalf of guests. Manager and above have this by default.</span>
                </label>
              </div>
            )}
            <div className="border border-border bg-muted/20 p-4">
              <Label className="overline text-primary/60">Service coverage & Apple Watch</Label>
              {form.crew_id ? (() => {
                const linked = crew.find((c) => c.id === form.crew_id);
                const assignments = linked?.service_assignments || [];
                return (
                  <>
                    <p className="text-xs text-muted-foreground mt-1 mb-3">Inherited from <span className="font-medium text-foreground">{linked?.name || "linked crew profile"}</span>. Edit service coverage in Crew, not here.</p>
                    <div className="flex flex-wrap gap-2">
                      {assignments.length === 0 && <span className="text-xs text-muted-foreground italic">No services assigned.</span>}
                      {assignments.map((row) => {
                        const m = modules.find((x) => x.id === row.module_id);
                        return <Badge key={row.module_id} variant="outline">{m?.name || row.module_id}{row.title ? ` · ${row.title}` : ""}</Badge>;
                      })}
                    </div>
                  </>
                );
              })() : (
                <>
                  <p className="text-xs text-muted-foreground mt-1 mb-3">This account is not linked to a crew profile. Manual alert scope remains available for service/kiosk accounts; leaving all boxes clear means all active modules.</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {modules.map((m) => {
                      const checked = (form.engaged_modules || []).includes(m.id);
                      return (
                        <label key={m.id} className="flex items-center gap-2 text-sm border border-border bg-card px-3 py-2">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => setForm((f) => ({ ...f, engaged_modules: e.target.checked ? [...(f.engaged_modules || []), m.id] : (f.engaged_modules || []).filter((x) => x !== m.id) }))}
                          />
                          <span>{m.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <div>
              <Label className="overline text-primary/60 flex items-center gap-1.5"><Anchor className="h-3 w-3 text-accent" /> Yacht rank / epaulet <span className="normal-case tracking-normal text-muted-foreground text-[10px]">(optional)</span></Label>
              <Select value={form.rank || "none"} onValueChange={(v) => setForm({ ...form, rank: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-2" data-testid="uf-rank"><SelectValue placeholder="Select a rank" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none" data-testid="uf-rank-none">— none —</SelectItem>
                  {YACHT_RANKS.map((group) => (
                    <SelectGroup key={group.department}>
                      <SelectLabel className="text-[10px] uppercase tracking-widest text-accent/80">{group.department}</SelectLabel>
                      {group.ranks.map((r) => (
                        <SelectItem key={r} value={r} data-testid={`uf-rank-opt-${r.replace(/\s+/g, '-').toLowerCase()}`}>{r}</SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Shown on the crew profile — decorative only, no permission impact.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="uf-save">
                {saving ? "Saving…" : (isEditing ? "Save changes" : "Create account")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
